import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { axios } from "..";
import { IPagination2, ISorting } from "../interfaces/Components/ITable";
import type { QuickFilterTotals } from "../components/Organismos/Table/FilteredTable.types";

function asFilterRecord (value: unknown): Record<string, unknown> {
  if (value == null || Array.isArray(value)) {
    return {};
  }
  if (typeof value === "object") {
    return value as Record<string, unknown>;
  }
  return {};
}

function parseCountFromResponseBody (body: unknown): number {
  if (typeof body === "number" && Number.isFinite(body)) {
    return body;
  }
  if (Array.isArray(body) && body.length > 0) {
    const v = body[0];
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof body === "string" && body.trim() !== "") {
    const n = Number(body);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

interface DataLoaderProps {
  endpoint: string;
  filters?: any;
  pagination?: IPagination2;
  quickFilter?: any;
  persistentFilter?: any;
  /**
   * Page-context filter used only for the one-shot chip totals request
   * (e.g. firmId / eventId). Must not include panel filters.
   */
  quickFilterTotalsPersistentFilter?: any;
  /** Canonical chip → filter clauses for `quickFilterTotal=true`. */
  quickFilterDefs?: Record<string, Record<string, unknown>>;
  sort?: ISorting;
  filterDebounceMs?: number;
  filterImmediateFlushKey?: number;
  /** When false, skips the extra `quickFilterTotal=true` request. */
  fetchQuickFilterTotals?: boolean;
  /** When true, skips all endpoint requests (client-side table data). */
  skipFetch?: boolean;
}

interface DataLoaderResult<T> {
  data: T[];
  totalCount: number;
  quickFilterTotalCount: QuickFilterTotals;
}

const DEFAULT_FILTER_DEBOUNCE_MS = 1000;

const useDataLoader = <T extends unknown> ({
  endpoint,
  filters,
  pagination,
  quickFilter,
  persistentFilter,
  quickFilterTotalsPersistentFilter,
  quickFilterDefs,
  sort,
  filterDebounceMs = DEFAULT_FILTER_DEBOUNCE_MS,
  filterImmediateFlushKey = 0,
  fetchQuickFilterTotals = true,
  skipFetch = false,
}: DataLoaderProps): DataLoaderResult<T> => {
  const [data, setData] = useState<T[]>([]);
  const [queryParams, setQueryParams] = useState<URLSearchParams>(
    new URLSearchParams(),
  );
  const [quickFilterTotalCount, setQuickFilterTotalCount] =
    useState<QuickFilterTotals>({});
  const [totalCount, settotalCount] = useState<number>(0);

  const latestRef = useRef({
    filters,
    quickFilter,
    persistentFilter,
    sort,
    pagination,
  });
  latestRef.current = {
    filters,
    quickFilter,
    persistentFilter,
    sort,
    pagination,
  };

  const filterDebounceSkipFirst = useRef(true);
  const prevFlushKeyRef = useRef<number | null>(null);
  /**
   * Chip counts freeze after a successful fetch for a given
   * endpoint + canonical defs + page-context persistent filter.
   * Panel filters are intentionally excluded from this key.
   */
  const totalsFetchedKeyRef = useRef<string | null>(null);

  const buildAndSetQueryParams = useCallback(() => {
    const {
      filters: f,
      quickFilter: qf,
      persistentFilter: pf,
      pagination: pg,
      sort: s,
    } = latestRef.current;
    const mergedFilter = {
      ...asFilterRecord(qf),
      ...asFilterRecord(f),
      ...asFilterRecord(pf),
    };
    // Chip key is UI-only; clauses come from panelValues / hiddenQuery.
    delete mergedFilter.quickFilter;
    const params = new URLSearchParams();
    if (Object.keys(mergedFilter).length > 0) {
      params.append("filter", JSON.stringify(mergedFilter));
    }
    if (pg) {
      params.append("pagination", JSON.stringify(pg));
    }
    if (s?.field) {
      params.append("sort", JSON.stringify(s));
    }

    const nextStr = params.toString();
    setQueryParams((prev) => (prev.toString() === nextStr ? prev : params));
  }, []);

  useLayoutEffect(() => {
    buildAndSetQueryParams();
  }, [pagination, sort, endpoint, buildAndSetQueryParams]);

  useLayoutEffect(() => {
    if (prevFlushKeyRef.current === null) {
      prevFlushKeyRef.current = filterImmediateFlushKey;
      return;
    }
    if (filterImmediateFlushKey === prevFlushKeyRef.current) {
      return;
    }
    prevFlushKeyRef.current = filterImmediateFlushKey;
    buildAndSetQueryParams();
  }, [filterImmediateFlushKey, buildAndSetQueryParams]);

  useLayoutEffect(() => {
    if (filterDebounceSkipFirst.current) {
      filterDebounceSkipFirst.current = false;
      return;
    }
    const id = window.setTimeout(() => {
      buildAndSetQueryParams();
    }, filterDebounceMs);
    return () => window.clearTimeout(id);
  }, [
    filters,
    quickFilter,
    persistentFilter,
    filterDebounceMs,
    buildAndSetQueryParams,
  ]);

  useEffect(() => {
    if (skipFetch) {
      setData([]);
      settotalCount(0);
      return () => { };
    }
    const hasPaginationParam = queryParams.has("pagination");
    if (!hasPaginationParam) {
      return () => { };
    }
    let cancelled = false;
    const run = async () => {
      const qs = queryParams.toString();
      const dataUrl = `${endpoint}?${qs}`;
      const countUrl = qs
        ? `${endpoint}?${qs}&count=true`
        : `${endpoint}?count=true`;
      const [dataResult, countResult] = await Promise.allSettled([
        axios.Get(dataUrl),
        axios.Get(countUrl),
      ]);
      if (cancelled) {
        return;
      }

      if (dataResult.status === "fulfilled") {
        setData(dataResult.value.data);
      } else {
        setData([]);
      }

      if (countResult.status === "fulfilled") {
        settotalCount(parseCountFromResponseBody(countResult.value.data));
      } else {
        const rows =
          dataResult.status === "fulfilled" &&
            Array.isArray(dataResult.value.data)
            ? dataResult.value.data.length
            : 0;
        settotalCount(rows);
      }
    };
    run().catch(() => {
      if (!cancelled) {
        setData([]);
        settotalCount(0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [queryParams, endpoint, skipFetch]);

  const forTotals = quickFilterTotalsPersistentFilter ?? persistentFilter;
  const quickTotalsPersistentSerialized = JSON.stringify(asFilterRecord(forTotals));
  const quickFilterDefsSerialized = JSON.stringify(quickFilterDefs ?? {});
  const totalsRequestKey = `${endpoint}::${quickFilterDefsSerialized}::${quickTotalsPersistentSerialized}`;

  useEffect(() => {
    if (skipFetch || !fetchQuickFilterTotals) {
      totalsFetchedKeyRef.current = null;
      setQuickFilterTotalCount({});
      return () => { };
    }

    const defs =
      quickFilterDefsSerialized === "{}"
        ? {}
        : (JSON.parse(quickFilterDefsSerialized) as Record<string, unknown>);
    if (Object.keys(defs).length === 0) {
      return () => { };
    }

    if (totalsFetchedKeyRef.current === totalsRequestKey) {
      return () => { };
    }

    const params = new URLSearchParams();
    params.append("quickFilterTotal", "true");
    params.append("quickFilterDefs", quickFilterDefsSerialized);
    const pfObj =
      quickTotalsPersistentSerialized === "{}"
        ? {}
        : (JSON.parse(quickTotalsPersistentSerialized) as Record<string, unknown>);
    delete pfObj.quickFilter;
    if (Object.keys(pfObj).length > 0) {
      params.append("filter", JSON.stringify(pfObj));
    }

    let cancelled = false;
    const run = async () => {
      try {
        const response = await axios.Get(`${endpoint}?${params.toString()}`);
        if (!cancelled) {
          totalsFetchedKeyRef.current = totalsRequestKey;
          setQuickFilterTotalCount(response.data ?? {});
        }
      } catch {
        if (!cancelled) {
          setQuickFilterTotalCount({});
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    endpoint,
    fetchQuickFilterTotals,
    quickFilterDefsSerialized,
    quickTotalsPersistentSerialized,
    totalsRequestKey,
    skipFetch,
  ]);

  return { data, totalCount, quickFilterTotalCount };
};

export default useDataLoader;
